import { useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
  AiOutlineFileExcel,
  AiOutlineFileImage,
  AiOutlineFilePdf,
  AiOutlineFileText,
  AiOutlineFileUnknown,
  AiOutlineFileWord,
} from "react-icons/ai";
import { FiDownload } from "react-icons/fi";
import { RiDeleteBin6Line } from "react-icons/ri";
import { Box } from "../Box";
import { Flex } from "../Flex";
import { Loader } from "../Loader";
import { Tooltip } from "../Tooltip";
import { formatDocumentTimestamp } from "../../utils/formatters";

/**
 * Shared list for uploaded documents.
 *
 * Replaces two divergent renderings of the same thing: the Profile Documents
 * tab, where every document was a shadowed card wrapping an ExpansionPanel
 * whose entire payload was the one-line description, and the Uploaded
 * Documents side panel, where the same row block was written out twice with
 * the label rendered heavier than the value it labelled.
 *
 * The row is a fixed-layout table, matching FormAnswers, so the type, date and
 * action columns sit on the same axis in every row regardless of how long a
 * filename runs. Percentage-width flex rows (the previous approach) shrink by
 * different amounts per row and the columns drift.
 *
 * The description is a muted second line rather than accordion content, which
 * is what removes the expand arrow: it is one short field and never justified
 * an interaction to reveal. Actions are inline icon buttons for the same
 * reason: with only download and delete, a menu costs a click and, in the
 * previous hand-positioned portal, drifted away from its row on scroll.
 */

export interface DocumentListItem {
  id?: number | string;
  /** Filename shown as the row title. */
  fileName: string;
  /** Signed media URL. When absent, the download action is hidden. */
  media?: string;
  /** Document type / category, shown in its own column. */
  type?: string;
  /** Free text, rendered as the muted second line under the filename. */
  description?: string;
  /** Raw created_on from the API, in any shape the endpoints return. */
  uploadedOn?: string;
  /** The untouched API object, handed back to the action callbacks. */
  raw?: any;
}

export interface DocumentGroup {
  title: string;
  documents: DocumentListItem[];
}

interface DocumentListProps {
  /** Flat list. Ignored when `groups` is given. */
  documents?: DocumentListItem[];
  /** Grouped list, rendered as labelled runs within one continuous list. */
  groups?: DocumentGroup[];
  onDownload: (document: DocumentListItem) => void;
  /** Omit to render the list without a delete action. */
  onDelete?: (document: DocumentListItem) => void;
  isLoading?: boolean;
  emptyMessage?: string;
  /** Richer empty state (e.g. an illustration). Wins over `emptyMessage`. */
  emptyState?: React.ReactNode;
  /** Hide the type column where every row shares one type. */
  showType?: boolean;
}

/* ------------------------------------------------------------------- icons */

const FILE_ICONS: Record<string, any> = {
  pdf: AiOutlineFilePdf,
  doc: AiOutlineFileWord,
  docx: AiOutlineFileWord,
  xls: AiOutlineFileExcel,
  xlsx: AiOutlineFileExcel,
  csv: AiOutlineFileExcel,
  jpg: AiOutlineFileImage,
  jpeg: AiOutlineFileImage,
  png: AiOutlineFileImage,
  gif: AiOutlineFileImage,
  txt: AiOutlineFileText,
};

/** Extension from a filename or a signed URL, lowercased, without the dot. */
export const fileExtension = (value?: string) => {
  if (!value) return "";
  const withoutQuery = value.split("?")[0];
  const lastSegment = withoutQuery.split("/").pop() ?? "";
  const parts = lastSegment.split(".");
  return parts.length > 1 ? parts.pop()!.toLowerCase() : "";
};

const FileGlyph = styled.div`
  ${({ theme: { colors, radii } }) => `
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 32px;
    height: 32px;
    border-radius: ${radii[0]};
    background: ${colors.gray[10]};
    color: ${colors.gray[60]};
    font-size: 17px;
  `}
`;

/* -------------------------------------------------------------------- rows */

const ROW_BLEED = "12px";

const Table = styled.table`
  ${({ theme: { colors } }) => `
    width: calc(100% + ${ROW_BLEED} * 2);
    margin: 0 -${ROW_BLEED};
    table-layout: fixed;
    border-collapse: separate;
    border-spacing: 0;
    font-family: "Inter", sans-serif;

    tbody tr:not(:last-of-type) td {
      border-bottom: 1px solid ${colors.gray[100]};
    }
  `}
`;

/**
 * The action buttons fade in on row hover on a pointer device, so a long list
 * reads as filenames rather than a column of repeated icons. They stay
 * permanently visible where hover is unavailable, and focus reveals them so
 * they remain reachable by keyboard.
 */
const Row = styled.tr`
  ${({ theme: { colors } }) => `
    &:hover td {
      background: ${colors.gray[5]};
    }

    @media (hover: hover) {
      .document-actions {
        opacity: 0;
        transition: opacity 0.15s ease-in-out;
      }

      &:hover .document-actions,
      &:focus-within .document-actions {
        opacity: 1;
      }
    }
  `}
`;

const Cell = styled.td<{ align?: string; width?: string }>`
  ${({ theme: { space }, align, width }) => `
    padding: ${space[1]} ${ROW_BLEED};
    vertical-align: middle;
    text-align: ${align ?? "left"};
    ${width ? `width: ${width};` : ""}
  `}
`;

const FileName = styled.div`
  ${({ theme: { colors, fontWeights } }) => `
    font-size: 14px;
    font-weight: ${fontWeights.medium};
    line-height: 1.35;
    color: ${colors.darkBlue};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `}
`;

const Description = styled.div`
  ${({ theme: { colors } }) => `
    margin-top: 2px;
    font-size: 12px;
    line-height: 1.35;
    color: ${colors.gray[50]};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `}
`;

const MetaText = styled.div`
  ${({ theme: { colors } }) => `
    font-size: 13px;
    color: ${colors.gray[60]};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `}
`;

/**
 * Nothing sets a global font-family in this app, so anything outside the
 * Table (which declares its own) inherits the browser default serif. Both the
 * group headers and the empty state sit outside it, so both state Inter.
 */
const GroupTitle = styled.div`
  ${({ theme: { colors, space } }) => `
    padding: ${space[2]} 0 ${space[0]};
    font-family: "Inter", sans-serif;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: ${colors.gray[60]};
  `}
`;

const ActionButton = styled.button<{ intent?: "danger" }>`
  ${({ theme: { colors, radii }, intent }) => `
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    padding: 0;
    border: none;
    background: transparent;
    border-radius: ${radii[0]};
    cursor: pointer;
    font-size: 16px;
    color: ${intent === "danger" ? colors.red.primary : colors.gray[60]};

    &:hover {
      background: ${intent === "danger" ? colors.red[50] : colors.gray[10]};
      color: ${intent === "danger" ? colors.red[40] : colors.blue.primary};
    }
  `}
`;

const EmptyState = styled.div`
  ${({ theme: { colors, space } }) => `
    padding: ${space[4]} 0;
    text-align: center;
    font-family: "Inter", sans-serif;
    font-size: 14px;
    color: ${colors.gray[50]};
  `}
`;

/* ------------------------------------------------------------------ render */

/**
 * Tooltip's own class is `display: inline-block`, which would shrink-wrap the
 * text to its full length and defeat the very ellipsis the tooltip exists to
 * explain. The wrapper is always rendered, tooltip or not, so the row lays out
 * identically either way.
 */
const ClipBox = styled.div`
  min-width: 0;

  & > .tooltip {
    display: block;
    max-width: 100%;
  }
`;

/**
 * Single-line text that only carries a tooltip when it is actually clipped.
 * Wrapping every filename unconditionally would pop a tooltip repeating text
 * already fully on screen, which is exactly the sort of noise this list is
 * meant to remove. Measured on mount: the row widths are fixed by the table,
 * so the answer only changes when the text does.
 */
const ClippedText = ({
  children,
  render,
}: {
  children: string;
  render: (ref: React.Ref<HTMLDivElement>) => React.ReactElement;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isClipped, setIsClipped] = useState(false);

  useLayoutEffect(() => {
    const element = ref.current;
    if (element) setIsClipped(element.scrollWidth > element.clientWidth);
  }, [children]);

  const element = render(ref);

  return (
    <ClipBox>
      {isClipped ? <Tooltip content={children}>{element}</Tooltip> : element}
    </ClipBox>
  );
};

const DocumentRow = ({
  document,
  onDownload,
  onDelete,
  showType,
}: {
  document: DocumentListItem;
  onDownload: (document: DocumentListItem) => void;
  onDelete?: (document: DocumentListItem) => void;
  showType: boolean;
}) => {
  // The filename is the better source, but some endpoints label a row with a
  // display name carrying no extension, so fall back to the media URL.
  const extension =
    fileExtension(document.fileName) || fileExtension(document.media);
  const Glyph = FILE_ICONS[extension] ?? AiOutlineFileUnknown;
  const description = document.description?.trim();

  return (
    <Row>
      <Cell>
        <Flex alignItems="center" gap="12">
          <FileGlyph>
            <Glyph />
          </FileGlyph>
          <Box minWidth="0" flex="1">
            <ClippedText
              render={(ref) => (
                <FileName ref={ref}>{document.fileName}</FileName>
              )}
            >
              {document.fileName}
            </ClippedText>
            {description ? (
              <ClippedText
                render={(ref) => (
                  <Description ref={ref}>{description}</Description>
                )}
              >
                {description}
              </ClippedText>
            ) : null}
          </Box>
        </Flex>
      </Cell>

      {showType && (
        <Cell width="22%">
          <MetaText>{document.type || "—"}</MetaText>
        </Cell>
      )}

      {/* Fixed, not a percentage: the timestamp is a fixed-length string, so a
          proportional column clips it on a narrow container however wide the
          container gets. 140px holds "28/08/2026 15:58" plus cell padding. */}
      <Cell width="140px">
        <MetaText>{formatDocumentTimestamp(document.uploadedOn)}</MetaText>
      </Cell>

      <Cell width="92px" align="right">
        <Flex
          className="document-actions"
          alignItems="center"
          justifyContent="flex-end"
          gap="4"
        >
          {document.media && (
            <Tooltip content="Download">
              <ActionButton
                type="button"
                aria-label={`Download ${document.fileName}`}
                onClick={() => onDownload(document)}
              >
                <FiDownload />
              </ActionButton>
            </Tooltip>
          )}
          {onDelete && (
            <Tooltip content="Delete">
              <ActionButton
                type="button"
                intent="danger"
                aria-label={`Delete ${document.fileName}`}
                onClick={() => onDelete(document)}
              >
                <RiDeleteBin6Line />
              </ActionButton>
            </Tooltip>
          )}
        </Flex>
      </Cell>
    </Row>
  );
};

export const DocumentList = (props: DocumentListProps) => {
  const {
    documents,
    groups,
    onDownload,
    onDelete,
    isLoading,
    emptyMessage = "No documents found.",
    emptyState,
    showType = true,
  } = props;

  if (isLoading) {
    return (
      <Flex justifyContent="center" alignItems="center" minHeight="20vh">
        <Loader />
      </Flex>
    );
  }

  const populatedGroups = (groups ?? []).filter(
    (group) => group.documents.length > 0
  );
  const hasRows = groups
    ? populatedGroups.length > 0
    : Boolean(documents?.length);

  if (!hasRows) {
    return emptyState ? (
      <>{emptyState}</>
    ) : (
      <EmptyState>{emptyMessage}</EmptyState>
    );
  }

  const renderTable = (rows: DocumentListItem[]) => (
    <Table>
      <tbody>
        {rows.map((document, index) => (
          <DocumentRow
            key={
              document.id ?? document.media ?? `${document.fileName}-${index}`
            }
            document={document}
            onDownload={onDownload}
            onDelete={onDelete}
            showType={showType}
          />
        ))}
      </tbody>
    </Table>
  );

  if (groups) {
    return (
      <Box>
        {populatedGroups.map((group) => (
          <Box key={group.title} mb={2}>
            <GroupTitle>{group.title}</GroupTitle>
            {renderTable(group.documents)}
          </Box>
        ))}
      </Box>
    );
  }

  return renderTable(documents ?? []);
};
